import React, { useEffect, useState } from 'react'
import { Card, Typography, Button, Space, Divider, Alert, Spin, Tag } from 'antd'
import { useParams, useNavigate } from 'react-router-dom'
import caseApi from '../../api/cases'
import type { GuidanceResponse } from '../../types'
import { ClockCircleOutlined, FileTextOutlined, BookOutlined, CheckCircleOutlined } from '@ant-design/icons'

const { Title, Paragraph } = Typography

const Guidance: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>()
  const [guidance, setGuidance] = useState<GuidanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    if (caseId) {
      caseApi.getGuidance(parseInt(caseId))
        .then(setGuidance)
        .catch(() => {
          // 如果没有匹配的救济途径
        })
        .finally(() => setLoading(false))
    }
  }, [caseId])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
        <Paragraph>正在分析您的案件...</Paragraph>
      </div>
    )
  }

  if (!guidance) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
        <Alert
          message="未找到匹配的救济途径"
          description="根据您的案件信息，暂时无法匹配到相应的救济途径规则。建议咨询专业法律人士或前往法院诉讼服务中心咨询。"
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />
        <Button onClick={() => navigate('/case/new')}>
          重新填写
        </Button>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      <Card style={{ marginBottom: 20 }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <Title level={2}>救济途径引导</Title>
        </div>

        <Alert
          message={guidance.remedy_type}
          type="success"
          icon={<CheckCircleOutlined />}
          showIcon
          style={{ marginBottom: 30, fontSize: 18 }}
        />

        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card>
            <Space>
              <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              <div>
                <Paragraph strong style={{ margin: 0 }}>管辖机关</Paragraph>
                <Paragraph style={{ margin: 0, fontSize: 16 }}>{guidance.authority}</Paragraph>
              </div>
            </Space>
          </Card>

          <Card>
            <Space>
              <ClockCircleOutlined style={{ fontSize: 24, color: '#fa8c16' }} />
              <div>
                <Paragraph strong style={{ margin: 0 }}>申请期限</Paragraph>
                <Paragraph style={{ margin: 0 }}>
                  <Tag color="orange">{guidance.deadline_days} 天</Tag>
                  <span style={{ fontSize: 16, marginLeft: 8 }}>
                    截止日期：{guidance.deadline_date}
                  </span>
                </Paragraph>
                {guidance.deadline_days === 0 && (
                  <Alert
                    message="申诉无固定期限限制，但建议尽早提出"
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </Space>
          </Card>

          <Card>
            <Space>
              <FileTextOutlined style={{ fontSize: 24, color: '#52c41a' }} />
              <div>
                <Paragraph strong style={{ margin: 0 }}>需要准备的材料</Paragraph>
                <Paragraph style={{ margin: 0, whiteSpace: 'pre-line' }}>
                  {guidance.materials_required}
                </Paragraph>
              </div>
            </Space>
          </Card>

          <Card>
            <Space>
              <BookOutlined style={{ fontSize: 24, color: '#722ed1' }} />
              <div>
                <Paragraph strong style={{ margin: 0 }}>法律依据</Paragraph>
                <Paragraph style={{ margin: 0 }}>{guidance.legal_basis}</Paragraph>
              </div>
            </Space>
          </Card>
        </Space>

        <Divider />

        <Card style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-line' }}>
            {guidance.guidance_text}
          </Paragraph>
        </Card>

        <div style={{ marginTop: 30, textAlign: 'center' }}>
          <Space>
            <Button onClick={() => navigate('/')}>
              返回首页
            </Button>
            <Button type="primary" onClick={() => window.print()}>
              打印引导结果
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  )
}

export default Guidance
